import { useRef, useState } from "react";
import { useNowClock } from "../home/externalLiveNow.js";
import { launcher } from "./launcherApi.js";
import { boxTiles, networkLabel, type BoxTile } from "./launcherTiles.js";
import { QuickSettings } from "./QuickSettings.js";
import { useLauncherHome } from "./useLauncherHome.js";

/**
 * Launcher mode's links in the top bar, beside search (owner, 2026-10-20: "your tv row should be
 * another nav bar at the top of the screen with search. just word links"): Quick settings first,
 * then Android settings, the stores and the plugged-in inputs, then the clock and the network.
 *
 * Plain buttons in the top bar, so the remote reaches them the way it reaches search and the
 * bell. Quick settings is a popup: Back closes it and focus returns to its link.
 */
export function LauncherLinks() {
  const { home } = useLauncherHome();
  const now = useNowClock();
  const [quickOpen, setQuickOpen] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const quickRef = useRef<HTMLButtonElement>(null);
  if (!home) return null;

  const failed = (what: string) => () => setNote(`${what} did not open on this box.`);
  const press = (tile: BoxTile) => {
    setNote(null);
    if (tile.kind === "quick") setQuickOpen(true);
    else if (tile.kind === "settings") void launcher.openSettings("all").catch(failed("Android settings"));
    else if (tile.kind === "app") void launcher.openApp(tile.package).catch(failed(tile.label));
    else void launcher.openInput(tile.id).catch(failed(tile.label));
  };
  const time = new Date(now).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

  return (
    <>
      <nav className="topbar-links" aria-label="Your TV">
        {boxTiles(home).map((tile, index) => (
          <button
            key={`${tile.kind}-${tile.label}`}
            ref={index === 0 ? quickRef : undefined}
            type="button"
            className="topbar-link"
            onClick={() => press(tile)}
            aria-label={tile.kind === "input" && tile.state === "standby" ? `${tile.label} (standby)` : undefined}
          >
            {tile.label}
          </button>
        ))}
        {note && <span className="topbar-links__note" role="status">{note}</span>}
        <QuickSettings
          open={quickOpen}
          onClose={() => {
            setQuickOpen(false);
            quickRef.current?.focus();
          }}
        />
      </nav>
      {/* Outside the links, so it is never squeezed: the links scroll instead when the bar is full
          (it read "2:3" on the onn box). The time only: Wi-Fi and Bluetooth are in Quick settings. */}
      <span className="topbar-links__status" aria-label={`${time}, ${networkLabel(home.status)}`}>
        {time}
      </span>
    </>
  );
}
